import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/app/utils/useLang';
import gtm from '@/utils/gtm';
import EcoImg from 'eco-design/base/eco-img';
import { useEffect, useRef, useState } from 'react';
import CommonTitle from '../CommonTitle';
import styles from './index.module.scss';
import useDeviceDetect from '@/utils/hooks/useDeviceDetect';
import EcoVideo from 'eco-design/base/eco-video';
import { useInViewport } from 'ahooks';
import videoLocaleMap, { imgLocaleOpenMap } from './config';

const PowerIcon = ({ size = '32', fill = '#010101', ...other }) => {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" {...other}>
            <path d="M16 32c8.837 0 16-7.163 16-16S24.837 0 16 0 0 7.163 0 16s7.163 16 16 16Z" fill="#DFDFDF" />

            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.936 10.608c1.742 1.47 2.613 3.328 2.613 5.575 0 2.038-.74 3.777-2.219 5.217-1.473 1.433-3.25 2.15-5.33 2.15-2.081 0-3.858-.717-5.332-2.15-1.48-1.44-2.219-3.179-2.219-5.217 0-2.217.852-4.06 2.557-5.527l.606-.522.522.607.522.606-.606.521c-1.334 1.149-2 2.587-2 4.315 0 1.588.577 2.945 1.734 4.07 1.163 1.131 2.568 1.697 4.215 1.697 1.647 0 3.053-.566 4.215-1.697 1.157-1.125 1.735-2.482 1.735-4.07 0-1.751-.682-3.202-2.045-4.352l-.612-.516.516-.612.516-.611.612.516Z"
                fill={fill}
            />

            <path fillRule="evenodd" clipRule="evenodd" d="M15.2 8.45v7.803h1.6V8.45h-1.6Z" fill={fill} />
        </svg>
    );
};
interface PriorityProps {
    pageType: 'ultra' | 'max';
}

const PriorityOutputs1: React.FC<PriorityProps> = ({ pageType }: PriorityProps = { pageType: 'max' }) => {
    const { locale } = getLocal();
    const { t } = useLang('PriorityOutputs1');
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const videoMap = videoLocaleMap[locale as 'us' | 'ca']?.[pageType];

    const { isPad, isH5 } = useDeviceDetect();

    const imgOpenMap = imgLocaleOpenMap[locale as 'us' | 'ca'];

    // 预加载单路视频和图片，实现点击按钮快速切换
    function preLoadImg() {
        const img = new Image();
        img.src = (isH5 ? imgOpenMap?.single?.h5 : imgOpenMap?.single?.pc) || '';
    }

    // 滚动到视口自动播放一次
    useEffect(() => {
        const triggerElement = document.querySelector('#PriorityOutputs .float-btn');
        const video = document.querySelector('.bg.video-1');

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    (video as HTMLVideoElement)?.play();
                }
            });
        });
        if (triggerElement) {
            observer.observe(triggerElement as Element);
        }
    }, []);

    useEffect(() => {
        const video = document.querySelector('.bg.video-1');
        const video2 = document.querySelector('.bg.video-2');

        if (video) {
            // 从头开始播放
            // @ts-expect-error
            video.currentTime = 0;
            // @ts-expect-error
            video.play();
        }
        if (video2) {
            // @ts-expect-error
            video2.currentTime = 0;
            // @ts-expect-error
            video2.play();
        }
    }, [isOpen]);

    const [inViewport] = useInViewport(containerRef);

    const [video1Src, setVideo1Src] = useState('');
    const [video1Poster, setVideo1Poster] = useState('');
    const [video2Src, setVideo2Src] = useState('');
    const [video2Poster, setVideo2Poster] = useState('');
    useEffect(() => {
        if (inViewport) {
            preLoadImg();
            if (window.innerWidth < 1024) {
                setVideo1Src(videoMap?.double?.h5 || '');
                setVideo1Poster(videoMap?.double?.h5Poster || '');
                setVideo2Src(videoMap?.single?.h5 || '');
                setVideo2Poster(videoMap?.single?.h5Poster || '');
            } else {
                setVideo1Src(videoMap?.double?.pc || '');
                setVideo1Poster(videoMap?.double?.pcPoster || '');
                setVideo2Src(videoMap?.single?.pc || '');
                setVideo2Poster(videoMap?.single?.pcPoster || '');
            }
        }
    }, [inViewport]);

    return (
        <div ref={containerRef} className={styles.PriorityOutputs1} data-section-id="d3m-priority-outputs1">
            <div className={`bg-video-container isOpen-${isOpen}`}>
                <video
                    key={1}
                    className="bg video-1"
                    src={video1Src}
                    poster={video1Poster}
                    playsInline
                    muted
                    // autoPlay
                    // loop
                    preload="auto"
                ></video>

                <video
                    key={2}
                    className="bg video-2"
                    preload="auto"
                    src={video2Src}
                    poster={video2Poster}
                    playsInline
                    muted
                    // autoPlay
                    // loop
                ></video>
            </div>

            {/* <EcoImg className="bg" pc="https://websiteoss.ecoflow.com/figma-uploads/1757488979318-xrfwk1-compressed-%E9%94%80%E5%94%AE%E6%A8%A1%E5%9D%97.jpg" /> */}
            <EcoImg
                className="phone"
                pc={!isOpen ? imgOpenMap?.double?.pc : imgOpenMap?.single?.pc}
                pad={!isOpen ? imgOpenMap?.double?.pad : imgOpenMap?.single?.pad}
                h5={!isOpen ? imgOpenMap?.double?.h5 : imgOpenMap?.single?.h5}
                isOpenLazy={false}
            />

            <div className="float-content">
                <div className="float-btn-wrapper">
                    <div
                        className="float-btn GtmModuleData-btn-text"
                        onClick={() => {
                            setIsOpen(!isOpen);

                            gtm.push({
                                event: 'uaEvent',
                                event_name: 'tab_click',
                                list_name: 'none',
                                button_name: 'btn',
                                __btn_id1: `${styles.PriorityOutputs1} .float-btn`,
                                __btn_id2: 'Click here to extend the runtime of the fridge.',
                                __btn_type: 'text',
                            });
                        }}
                    >
                        <PowerIcon />
                    </div>
                    <div className="float-mask-1"></div>
                    <div className="float-mask-2"></div>
                    <div className="tips">{t('tip')}</div>
                </div>
            </div>

            {/* 开发环境下的位置调试器 */}
            {/* {isCalculated && <PositionDebugger position={position} containerRef={containerRef} imageSize={PHONE_IMAGE_CONFIG.imageSize} isVisible={true} />} */}

            <div className="content">
                {/* <EcoSpace hPc="100px" hPad="50px" hH5="60px" /> */}
                <CommonTitle title={t('title')} desc={t('desc')} sub_title={t('sub_title')} />
            </div>
        </div>
    );
};

export default PriorityOutputs1;
